import "./styles.css";

import { Recorder } from "./recorder";
import { clear, drawAll, fit, prepare, segment } from "./render";
import { play, SITE_SPEED_FACTOR, type Playback } from "./player";
import { download, downloadText, toJSON, toPNG, toSVG } from "./export";
import { canShare, share, signatureFile } from "./share";
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, parseSignatureData } from "./types";

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
};

const stage = $("stage");
const pad = $<HTMLCanvasElement>("pad");
const replayCanvas = $<HTMLCanvasElement>("replay");
const hint = $("hint");
const stats = $("stats");
const status = $("status");
const speed = $<HTMLInputElement>("speed");
const speedOut = $<HTMLOutputElement>("speed-out");
const file = $<HTMLInputElement>("file");

const shareRow = $("share-row");

const button = {
  undo: $<HTMLButtonElement>("undo"),
  redo: $<HTMLButtonElement>("redo"),
  clear: $<HTMLButtonElement>("clear"),
  play: $<HTMLButtonElement>("play"),
  share: $<HTMLButtonElement>("share"),
  json: $<HTMLButtonElement>("export-json"),
  svg: $<HTMLButtonElement>("export-svg"),
  png: $<HTMLButtonElement>("export-png"),
};

/**
 * Probe the share sheet once, with a representative file. A dummy payload is enough:
 * canShare() inspects the file's type, not its contents, and doing it up front means
 * the button's presence is settled before anyone signs.
 */
const SHARING_AVAILABLE = canShare(
  new File(["{}"], "signature.json", { type: "application/json" }),
);

if (SHARING_AVAILABLE) {
  shareRow.hidden = false;
  document.body.classList.add("can-share");
  // Only one action can be the obvious one, and when you can share, sharing is it.
  $("export-json").classList.remove("primary");
}

const padCtx = prepare(pad);
const replayCtx = prepare(replayCanvas);
const recorder = new Recorder();

let playback: Playback | null = null;
let statusTimer = 0;

clear(padCtx);

// --- status line ------------------------------------------------------------

function say(message: string, isError = false): void {
  status.textContent = message;
  status.className = isError ? "show error" : "show";
  window.clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => (status.className = ""), isError ? 6000 : 2500);
}

function hush(): void {
  window.clearTimeout(statusTimer);
  status.className = "";
}

// --- state sync -------------------------------------------------------------

function sync(): void {
  const points = recorder.points().length;
  const strokes = recorder.all().length;
  const empty = recorder.isEmpty;

  button.undo.disabled = !recorder.canUndo;
  button.redo.disabled = !recorder.canRedo;
  button.clear.disabled = empty;
  button.play.disabled = empty;
  button.share.disabled = empty;
  button.json.disabled = empty;
  button.svg.disabled = empty;
  button.png.disabled = empty;

  stats.textContent = empty
    ? "0 strokes · 0 points"
    : `${strokes} stroke${strokes === 1 ? "" : "s"} · ${points} points · ${(recorder.duration() / 1000).toFixed(2)}s`;

  stage.classList.toggle("dirty", !empty);
  hint.hidden = !empty;
}

// --- drawing ----------------------------------------------------------------

function stopPlayback(): void {
  playback?.cancel();
  playback = null;
  stage.classList.remove("replaying");
}

/**
 * Client coordinates into the fixed 400x240 capture space. The pad is displayed
 * smaller on a phone, so the ratio — not the raw offset — is what carries over.
 */
function positionOf(event: PointerEvent): { x: number; y: number } {
  const rect = pad.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * CAPTURE_WIDTH,
    y: ((event.clientY - rect.top) / rect.height) * CAPTURE_HEIGHT,
  };
}

const isPen = (event: PointerEvent): boolean => event.pointerType === "pen";

/**
 * Wall-clock time of the event itself, not of the moment we got round to handling
 * it. `Date.now()` reads the same for every point in a coalesced batch, which
 * loses the timing inside a fast stroke and leaves nothing able to distinguish a
 * fresh sample from one iPadOS has already delivered. `timeStamp` is per-event and
 * sub-millisecond; the origin puts it back on the epoch the format stores. Tenths
 * of a millisecond is as fine as it goes before epoch values run out of double.
 */
const clockOf = (event: PointerEvent): number =>
  Math.round((performance.timeOrigin + event.timeStamp) * 10) / 10;

pad.addEventListener("pointerdown", (event) => {
  // A pen hovering near the pad fires moves with no contact; only ink on a real press.
  if (event.button !== 0) return;
  stopPlayback();
  hush(); // a stale error shouldn't hang around once you're drawing again
  pad.setPointerCapture(event.pointerId);

  const { x, y } = positionOf(event);
  recorder.begin(x, y, event.pressure, isPen(event), clockOf(event));
  sync();
});

pad.addEventListener("pointermove", (event) => {
  if (!recorder.isDrawing) return;

  // Coalesced events recover the points the browser batched between frames;
  // without them a fast stroke comes out as a few long straight chords.
  const events = event.getCoalescedEvents?.() ?? [event];
  for (const e of events) {
    const { x, y } = positionOf(e);
    const drawn = recorder.extend(x, y, e.pressure, isPen(e), clockOf(e));
    if (drawn) segment(padCtx, drawn.from, drawn.to);
  }
  sync();
});

/**
 * Rotating an iPad changes the pad's size, and resizing a canvas clears it — so
 * refit both layers and put the signature back. Debounced through rAF because
 * iPadOS fires a burst of resize events through an orientation change.
 */
let refitting = 0;

function refit(): void {
  cancelAnimationFrame(refitting);
  refitting = requestAnimationFrame(() => {
    const padChanged = fit(pad, padCtx);
    const replayChanged = fit(replayCanvas, replayCtx);
    if (padChanged || replayChanged) drawAll(padCtx, recorder.all());
  });
}

window.addEventListener("resize", refit);
window.addEventListener("orientationchange", refit);

const finishStroke = () => {
  if (!recorder.isDrawing) return;
  recorder.end();
  // A discarded 1-point tap may still have left a dot on the canvas.
  drawAll(padCtx, recorder.all());
  sync();
};

pad.addEventListener("pointerup", finishStroke);
pad.addEventListener("pointercancel", finishStroke);

// --- actions ----------------------------------------------------------------

function redrawPad(): void {
  drawAll(padCtx, recorder.all());
  sync();
}

const speedFactor = () => Number(speed.value);

function startReplay(): void {
  if (recorder.isEmpty || recorder.isDrawing) return;
  stopPlayback();
  stage.classList.add("replaying");
  playback = play(replayCtx, recorder.all(), speedFactor(), () => {
    // Hold the finished signature on screen briefly before handing the pad back.
    window.setTimeout(stopPlayback, 700);
  });
}

button.undo.addEventListener("click", () => {
  recorder.undo();
  redrawPad();
});

button.redo.addEventListener("click", () => {
  recorder.redo();
  redrawPad();
});

button.clear.addEventListener("click", () => {
  stopPlayback();
  recorder.clear();
  redrawPad();
});

button.play.addEventListener("click", startReplay);

button.share.addEventListener("click", async () => {
  if (recorder.isEmpty) return;

  // Built synchronously so the share() call still counts as inside the user gesture.
  const file = signatureFile(recorder.toData());

  try {
    const outcome = await share(file);
    if (outcome === "shared") say("Sent — thank you");
    // "cancelled" means they backed out of the sheet; saying anything would be noise.
    if (outcome === "unsupported") {
      download(file, "signature.json");
      say("Downloaded — attach it to a message");
    }
  } catch (error) {
    // A genuine failure (not a dismissal): don't strand them, hand over the file.
    download(file, "signature.json");
    say(error instanceof Error ? `${error.message} — downloaded instead` : "Downloaded instead", true);
  }
});

button.json.addEventListener("click", () => {
  downloadText(toJSON(recorder.toData()), "signature.json", "application/json");
  say("signature.json exported");
});

button.svg.addEventListener("click", () => {
  downloadText(toSVG(recorder.all()), "signature.svg", "image/svg+xml");
  say("signature.svg exported");
});

button.png.addEventListener("click", async () => {
  try {
    download(await toPNG(recorder.all()), "signature.png");
    say("signature.png exported");
  } catch (error) {
    say(error instanceof Error ? error.message : "PNG export failed", true);
  }
});

speed.addEventListener("input", () => {
  const value = speedFactor();
  speedOut.textContent = value === SITE_SPEED_FACTOR ? `${value}× (site)` : `${value}×`;
});

// --- loading an existing signature.json -------------------------------------

async function load(source: File): Promise<void> {
  try {
    const data = parseSignatureData(JSON.parse(await source.text()));
    stopPlayback();
    recorder.load(data);
    redrawPad();
    say(`Loaded ${source.name}`);
    startReplay();
  } catch (error) {
    say(error instanceof Error ? error.message : "Could not read that file", true);
  }
}

file.addEventListener("change", () => {
  const chosen = file.files?.[0];
  if (chosen) void load(chosen);
  file.value = ""; // let the same file be picked twice in a row
});

stage.addEventListener("dragover", (event) => {
  event.preventDefault();
  stage.classList.add("dropping");
});

stage.addEventListener("dragleave", () => stage.classList.remove("dropping"));

stage.addEventListener("drop", (event) => {
  event.preventDefault();
  stage.classList.remove("dropping");
  const dropped = event.dataTransfer?.files?.[0];
  if (dropped) void load(dropped);
});

// --- keyboard ---------------------------------------------------------------

window.addEventListener("keydown", (event) => {
  const meta = event.metaKey || event.ctrlKey;

  if (meta && event.key.toLowerCase() === "z") {
    event.preventDefault();
    if (event.shiftKey) recorder.redo();
    else recorder.undo();
    redrawPad();
    return;
  }

  if (event.key === "Escape") {
    stopPlayback();
    recorder.clear();
    redrawPad();
    return;
  }

  if (event.code === "Space") {
    // Space would otherwise also click whichever button has focus.
    event.preventDefault();
    startReplay();
  }
});

sync();
